import {
  Box,
  BoxProps,
  Divider,
  Flex,
  Heading,
  Icon,
  Link,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalOverlay,
  Text,
  TextProps,
  useDisclosure,
  VStack,
} from "@chakra-ui/react";
import { DetailedHTMLProps, ReactNode, useMemo, useState } from "react";

import { LoyaltyConfig } from "custom-types";
import { FaChevronDown, FaCoins } from "react-icons/fa";
import { ImWarning } from "react-icons/im";

export interface AmountSummaryProps {
  headingText?: string;
  currency?: string;
  amountBreakdown: {
    title: string;
    amount: number;
    quantity: number;
    showAmountUnitPrice?: boolean;
    highlightItem?: boolean;
  }[];
  foodBreakdown?: {
    title: string;
    amount: number;
    quantity: number;
    showAmountUnitPrice?: boolean;
    highlightItem?: boolean;
  }[];
  ticketSubtotal?: number;
  foodSubtotal?: number;
  /**
   * Discount-voucher saving on this checkout, already applied to `total` by the caller.
   * Rendered as its own line so the customer can see what the code did rather than being shown a
   * total that silently disagrees with the prices above it. 0/undefined renders nothing.
   */
  voucherDiscount?: number;
  /** The code that produced it, so the line names the thing the customer typed. */
  voucherLabel?: string;
  /**
   * The part of `total` that actually earns loyalty points. Defaults to `total`.
   *
   * Food does not earn: dolby awards points against the MOVIE transaction only, so a basket of
   * tickets + food earns on the tickets alone. Projecting off `total` promised points on the food
   * that were never granted — the customer was quoted one figure at checkout and credited a
   * smaller one.
   */
  loyaltyEarnableAmount?: number;
  amountSummaryBoxProps?: BoxProps;
  amountSummaryTextProps?: TextProps;
  three_d_charge: number;
  loyaltyConfig: LoyaltyConfig | null;
  payViaLoyalty: boolean;
  availableLoyaltyPoints: number;
  /**
   * Points the member may still earn under the day/week/month caps, from the member payload.
   * `null`/undefined means those windows are uncapped. The per-transaction cap is applied
   * separately from `loyaltyConfig`.
   */
  loyaltyEarnAllowance?: number | null;
  /**
   * Per-window standing (cap, earned, remaining) rendered as the customer's earning limits.
   * Only windows with a configured cap are shown.
   */
  loyaltyLimits?: {
    label: string;
    cap: number;
    /** null when usage couldn't be loaded — the cap is still shown, the progress is not. */
    earned: number | null;
    remaining: number | null;
  }[];
  total: number;
  headingTextProps?: BoxProps;
  amountSummaryContainerProps?: BoxProps;
  VStackProps?: BoxProps;
  amountItemFlexProps?: BoxProps;
  amountItemTitleProps?: BoxProps;
  amountItemUnitPriceProps?: BoxProps;
  amountItemAmountBoxProps?: BoxProps;
  totalFlexProps?: BoxProps;
  totalTextProps?: BoxProps;
  totalAmountTextProps?: BoxProps;
  subTotalTextProps?: TextProps;
  subTotalAmountTextProps?: TextProps;
  //eslint-disable-next-line @typescript-eslint/no-explicit-any
  loyaltyContainerProps?: DetailedHTMLProps<any, any>;
  /** Surface styling for the earning-limits dialog (it is portalled, so it can't inherit the card's). */
  loyaltyDialogProps?: DetailedHTMLProps<any, any>;
  warningTextProps?: TextProps;
  loyaltyInfoTextProps?: TextProps;
  // ---- Loyalty card: configurable copy, icon, theming ----
  /** Heading of the loyalty card. Defaults to "Loyalty Points". */
  loyaltyTitle?: string;
  /** Icon shown next to the loyalty title. Defaults to a coins icon. */
  loyaltyIcon?: ReactNode;
  /** Label for the available-points stat. Defaults to "Available". */
  availablePointsLabel?: string;
  /** Label for the required-points stat. Defaults to "Required". */
  requiredPointsLabel?: string;
  /** Override the "not enough points" warning copy (static). */
  notEnoughPointsText?: string;
  /** Color token for positive point figures (earn / remaining). Default "success.500". */
  positivePointColor?: string;
  /** Color token for negative point figures (after spend). Default "danger.500". */
  negativePointColor?: string;
  loyaltyHeadingProps?: BoxProps;
  loyaltyTitleProps?: TextProps;
  loyaltyStatRowProps?: BoxProps;
  loyaltyStatLabelProps?: TextProps;
  loyaltyStatValueProps?: TextProps;
}

export function AmountSummary({
  headingText = "Amount Summary",
  currency = "Rs. ",
  amountBreakdown,
  foodBreakdown = [],
  ticketSubtotal = 0,
  foodSubtotal = 0,
  voucherDiscount = 0,
  voucherLabel,
  three_d_charge = 0,
  loyaltyConfig = null,
  payViaLoyalty = false,
  amountSummaryTextProps = {},
  availableLoyaltyPoints = 0,
  loyaltyEarnAllowance = null,
  loyaltyLimits = [],
  loyaltyEarnableAmount,
  total,
  headingTextProps,
  amountSummaryBoxProps,
  amountSummaryContainerProps,
  VStackProps,
  amountItemFlexProps,
  amountItemTitleProps,
  amountItemUnitPriceProps,
  subTotalTextProps,
  subTotalAmountTextProps,
  amountItemAmountBoxProps,
  totalFlexProps,
  totalTextProps,
  totalAmountTextProps,
  loyaltyContainerProps,
  loyaltyDialogProps,
  warningTextProps,
  loyaltyInfoTextProps,
  loyaltyTitle = "Loyalty Points",
  loyaltyIcon,
  availablePointsLabel = "Available",
  requiredPointsLabel = "Required",
  notEnoughPointsText,
  positivePointColor = "success.500",
  negativePointColor = "danger.500",
  loyaltyHeadingProps,
  loyaltyTitleProps,
  loyaltyStatRowProps,
  loyaltyStatLabelProps,
  loyaltyStatValueProps,
  ...props
}: AmountSummaryProps) {
  // What the booking earns *after* the configured caps. Dolby clamps the real award the same way
  // (applyEarningCaps in point-transaction.service.ts); projecting the uncapped figure here would
  // promise the customer points they will never receive.
  const uncappedRewardPoints = useMemo(() => {
    if (!loyaltyConfig) return 0;
    const { amount_to_point_conversion_rate = 0 } = loyaltyConfig;
    // `loyaltyEarnableAmount` when the caller knows only part of the basket earns; `total` keeps
    // every existing caller unchanged.
    const earnable = loyaltyEarnableAmount ?? total;

    return (amount_to_point_conversion_rate / 100) * Math.max(earnable, 0);
  }, [loyaltyConfig, total, loyaltyEarnableAmount]);

  // What the booking earns *after* the configured caps. Dolby clamps the real award the same way
  // (getEarningStatus in point-transaction.service.ts); projecting the uncapped figure here would
  // promise the customer points they will never receive.
  const rewardPoints = useMemo(() => {
    if (!loyaltyConfig) return 0;
    const { max_points_per_transaction = 0 } = loyaltyConfig;

    let points = uncappedRewardPoints;

    // 0 is the "unlimited" sentinel on every cap.
    if (max_points_per_transaction > 0) {
      points = Math.min(points, max_points_per_transaction);
    }
    // Headroom left in the day/week/month windows; null when those are uncapped.
    if (loyaltyEarnAllowance !== null && loyaltyEarnAllowance !== undefined) {
      points = Math.min(points, Math.max(loyaltyEarnAllowance, 0));
    }

    return Math.max(points, 0);
  }, [loyaltyConfig, uncappedRewardPoints, loyaltyEarnAllowance]);

  const isEarnCapped = rewardPoints < uncappedRewardPoints;

  const {
    isOpen: isLimitsOpen,
    onOpen: onLimitsOpen,
    onClose: onLimitsClose,
  } = useDisclosure();

  const perTransactionCap = loyaltyConfig?.max_points_per_transaction ?? 0;
  // Nothing to show unless at least one cap is actually configured.
  const showLimitLink = loyaltyLimits.length > 0 || perTransactionCap > 0;

  const requiredPoints = useMemo(() => {
    if (!loyaltyConfig) return 0;
    const { point_to_amount_conversion_rate = 0 } = loyaltyConfig;

    return (total * 100) / point_to_amount_conversion_rate;
  }, [loyaltyConfig, total]);

  const hasEnoughPoints = requiredPoints <= availableLoyaltyPoints;
  // Loyalty card is collapsible on mobile (closed by default); always open ≥ md.
  const [loyaltyOpen, setLoyaltyOpen] = useState(false);
  return (
    <>
      {loyaltyConfig?.enable &&
        !!loyaltyConfig?.point_to_amount_conversion_rate &&
        !!loyaltyConfig?.amount_to_point_conversion_rate && (
          <Box
            borderRadius="xl"
            overflow="hidden"
            boxShadow="sm"
            border="1px solid"
            borderColor="blackAlpha.200"
            mb={3}
            {...loyaltyContainerProps}
          >
            {/* Header (tap to expand/collapse on mobile) */}
            <Flex
              align="center"
              justify="space-between"
              gap={2}
              px={4}
              py={3}
              bg="primary.500"
              color="white"
              cursor={{ base: "pointer", md: "default" }}
              onClick={() => setLoyaltyOpen((v) => !v)}
              {...loyaltyHeadingProps}
            >
              <Flex align="center" gap={2}>
                {loyaltyIcon ?? <Icon as={FaCoins} boxSize="1.1rem" />}
                <Text fontSize="md" fontWeight="bold" {...loyaltyTitleProps}>
                  {loyaltyTitle}
                </Text>
              </Flex>
              <Icon
                as={FaChevronDown}
                display={{ base: "block", md: "none" }}
                boxSize="0.85rem"
                transition="transform 0.2s ease"
                transform={loyaltyOpen ? "rotate(180deg)" : "rotate(0deg)"}
              />
            </Flex>

            <Box
              display={{ base: loyaltyOpen ? "block" : "none", md: "block" }}
              px={4}
              py={3}
            >
              {/* Stat rows */}
              <Flex gap={3}>
                <Flex
                  flex={1}
                  direction="column"
                  align="center"
                  py={2}
                  borderRadius="lg"
                  bg="blackAlpha.50"
                  {...loyaltyStatRowProps}
                >
                  <Text
                    fontSize="xs"
                    opacity={0.7}
                    textTransform="uppercase"
                    letterSpacing="0.04em"
                    {...loyaltyStatLabelProps}
                  >
                    {availablePointsLabel}
                  </Text>
                  <Text
                    fontSize="xl"
                    fontWeight="bold"
                    {...loyaltyStatValueProps}
                  >
                    {availableLoyaltyPoints}
                  </Text>
                </Flex>
                <Flex
                  flex={1}
                  direction="column"
                  align="center"
                  py={2}
                  borderRadius="lg"
                  bg="blackAlpha.50"
                  {...loyaltyStatRowProps}
                >
                  <Text
                    fontSize="xs"
                    opacity={0.7}
                    textTransform="uppercase"
                    letterSpacing="0.04em"
                    {...loyaltyStatLabelProps}
                  >
                    {requiredPointsLabel}
                  </Text>
                  <Text
                    fontSize="xl"
                    fontWeight="bold"
                    color={hasEnoughPoints ? undefined : negativePointColor}
                    {...loyaltyStatValueProps}
                  >
                    {requiredPoints}
                  </Text>
                </Flex>
              </Flex>

              {!hasEnoughPoints && (
                <Flex
                  mt={3}
                  p={2}
                  borderRadius="md"
                  bg="rgba(255, 69, 48, 0.1)"
                  color={negativePointColor}
                  fontSize="sm"
                  gap={2}
                  align="flex-start"
                  {...warningTextProps}
                >
                  <Icon as={ImWarning} mt="0.15rem" flexShrink={0} />
                  <Text>
                    {notEnoughPointsText ??
                      `You don't have enough points — ${requiredPoints} are needed to cover this bill with loyalty points.`}
                  </Text>
                </Flex>
              )}

              <Box mt={3} fontSize="sm" opacity={0.85}>
                {!payViaLoyalty && (
                  <Text {...loyaltyInfoTextProps}>
                    You&apos;ll earn{" "}
                    <Text
                      as="span"
                      color={positivePointColor}
                      fontWeight="semibold"
                    >
                      +{rewardPoints}
                    </Text>{" "}
                    points from this booking.
                    {showLimitLink && (
                      <>
                        {" "}
                        <Link
                          as="button"
                          type="button"
                          onClick={onLimitsOpen}
                          textDecoration="underline"
                          fontWeight="semibold"
                          color={isEarnCapped ? negativePointColor : undefined}
                        >
                          {isEarnCapped ? "Limit reached" : "Limit"}
                        </Link>
                      </>
                    )}
                  </Text>
                )}
                <Text {...loyaltyInfoTextProps}>
                  After this booking, you&apos;ll have{" "}
                  <Text
                    as="span"
                    fontWeight="semibold"
                    color={
                      payViaLoyalty ? negativePointColor : positivePointColor
                    }
                  >
                    {payViaLoyalty
                      ? availableLoyaltyPoints - requiredPoints
                      : availableLoyaltyPoints + rewardPoints}
                  </Text>{" "}
                  points remaining.
                </Text>
              </Box>
            </Box>
          </Box>
        )}

      {/* Styled to read as an extension of the Loyalty Points card above: same primary.500 header
          with the coin icon, same blackAlpha tile/border tokens, and the same themeable
          positive/negative point colours so a tenant palette carries through. */}
      <Modal isOpen={isLimitsOpen} onClose={onLimitsClose} isCentered size="sm">
        <ModalOverlay />
        {/* The dialog is portalled out of the loyalty card, so it cannot inherit the card's
            surface through the DOM and would otherwise fall back to Chakra's white modal
            background. `loyaltyDialogProps` is how the caller hands it the same colour — kept
            separate from `loyaltyContainerProps` so theming the dialog can't restyle the card. */}
        <ModalContent
          borderRadius="xl"
          overflow="hidden"
          boxShadow="sm"
          border="1px solid"
          borderColor="blackAlpha.200"
          {...loyaltyDialogProps}
        >
          <Flex
            align="center"
            gap={2}
            px={4}
            py={3}
            bg="primary.500"
            color="white"
            {...loyaltyHeadingProps}
          >
            {loyaltyIcon ?? <Icon as={FaCoins} boxSize="1.1rem" />}
            <Text fontSize="md" fontWeight="bold" {...loyaltyTitleProps}>
              Earning Limits
            </Text>
          </Flex>
          <ModalCloseButton color="white" top="0.8rem" right="0.75rem" />

          <ModalBody px={4} py={3}>
            <VStack align="stretch" spacing={2}>
              {perTransactionCap > 0 && (
                <Flex
                  justify="space-between"
                  align="baseline"
                  gap={4}
                  px={3}
                  py={2}
                  borderRadius="lg"
                  bg="blackAlpha.50"
                  {...loyaltyStatRowProps}
                >
                  <Text
                    fontSize="xs"
                    opacity={0.7}
                    textTransform="uppercase"
                    letterSpacing="0.04em"
                    {...loyaltyStatLabelProps}
                  >
                    Per booking
                  </Text>
                  <Text
                    fontSize="md"
                    fontWeight="bold"
                    whiteSpace="nowrap"
                    {...loyaltyStatValueProps}
                  >
                    {perTransactionCap} max
                  </Text>
                </Flex>
              )}

              {loyaltyLimits.map((limit) => {
                const used = limit.earned ?? 0;
                const left = limit.remaining;
                const pct = Math.min(
                  100,
                  limit.cap > 0 ? (used / limit.cap) * 100 : 0,
                );
                const valueColor =
                  left === null
                    ? undefined
                    : left > 0
                      ? positivePointColor
                      : negativePointColor;

                return (
                  <Box
                    key={limit.label}
                    px={3}
                    py={2}
                    borderRadius="lg"
                    bg="blackAlpha.50"
                    {...loyaltyStatRowProps}
                  >
                    <Flex
                      justify="space-between"
                      align="baseline"
                      gap={4}
                      mb={left === null ? 0 : 1.5}
                    >
                      <Text
                        fontSize="xs"
                        opacity={0.7}
                        textTransform="uppercase"
                        letterSpacing="0.04em"
                        {...loyaltyStatLabelProps}
                      >
                        {limit.label}
                      </Text>
                      <Text
                        fontSize="md"
                        fontWeight="bold"
                        whiteSpace="nowrap"
                        color={valueColor}
                        {...loyaltyStatValueProps}
                      >
                        {left === null ? `${limit.cap} max` : `${left} left`}
                      </Text>
                    </Flex>

                    {left === null ? (
                      <Text fontSize="xs" opacity={0.6}>
                        Usage unavailable right now.
                      </Text>
                    ) : (
                      <>
                        <Box
                          h="4px"
                          borderRadius="full"
                          bg="blackAlpha.200"
                          overflow="hidden"
                        >
                          <Box
                            h="100%"
                            w={`${pct}%`}
                            borderRadius="full"
                            bg={valueColor}
                          />
                        </Box>
                        <Text fontSize="xs" opacity={0.7} mt={1}>
                          {used} of {limit.cap} points earned
                        </Text>
                      </>
                    )}
                  </Box>
                );
              })}
            </VStack>

            {loyaltyLimits.length === 0 && perTransactionCap > 0 && (
              <Text fontSize="xs" opacity={0.7} mt={3}>
                No daily, weekly or monthly limit applies.
              </Text>
            )}
          </ModalBody>
          <ModalFooter px={4} pt={0} pb={3}>
            <Text fontSize="xs" opacity={0.6}>
              Limits reset at the start of each day, week and month.
            </Text>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Box
        borderRadius="md"
        overflow="hidden"
        shadow="sm"
        {...props}
        {...amountSummaryBoxProps}
      >
        <Heading
          background="surface.10"
          color="surface.500"
          px={{ base: "1rem", md: "1.5rem" }}
          py="0.75rem"
          size={{ base: "sm", md: "md" }}
          {...amountSummaryTextProps}
          {...headingTextProps}
        >
          {headingText}
        </Heading>

        <Box
          px={{ base: "1rem", md: "1.5rem" }}
          py={{ base: "1.25rem", md: "2rem" }}
          {...amountSummaryContainerProps}
        >
          <Text
            fontSize="md"
            fontWeight="600"
            color="surface.500"
            mb="0.5rem"
            {...amountSummaryTextProps}
          >
            Tickets
          </Text>
          <VStack {...VStackProps}>
            {amountBreakdown.map((item, index) => (
              <Flex
                key={`amount__${index}`}
                w="full"
                justifyContent="space-between"
                {...amountItemFlexProps}
              >
                <Box>
                  <Text
                    fontSize={item.highlightItem ? "lg" : "md"}
                    color={item.highlightItem ? "surface.500" : "surface.300"}
                    {...amountSummaryTextProps}
                    {...amountItemTitleProps}
                  >
                    {item.title}
                    {item.showAmountUnitPrice && (
                      <Text
                        ml="0.5rem"
                        as="span"
                        {...amountSummaryTextProps}
                        {...amountItemUnitPriceProps}
                      >
                        ({item.quantity} X {item.amount})
                      </Text>
                    )}
                  </Text>
                </Box>
                <Box {...amountItemAmountBoxProps}>
                  <Text
                    fontWeight={500}
                    fontSize="md"
                    color="surface.300"
                    {...amountSummaryTextProps}
                  >
                    = {currency} {item.amount * item.quantity}
                  </Text>
                </Box>
              </Flex>
            ))}
          </VStack>

          {three_d_charge > 0 && (
            <>
              <VStack {...VStackProps} mt="0.5rem">
                {amountBreakdown.map((item, index) => (
                  <Flex
                    key={`three_d_amount__${index}`}
                    w="full"
                    justifyContent="space-between"
                    {...amountItemFlexProps}
                  >
                    <Box>
                      <Text
                        fontSize={item.highlightItem ? "lg" : "md"}
                        color={
                          item.highlightItem ? "surface.500" : "surface.300"
                        }
                        {...amountSummaryTextProps}
                        {...amountItemTitleProps}
                      >
                        3D Charge
                        {item.showAmountUnitPrice && (
                          <Text
                            ml="0.5rem"
                            as="span"
                            {...amountSummaryTextProps}
                            {...amountItemUnitPriceProps}
                          >
                            ({item.quantity} X {three_d_charge})
                          </Text>
                        )}
                      </Text>
                    </Box>
                    <Box>
                      <Text
                        fontWeight={500}
                        fontSize="md"
                        color="surface.300"
                        {...amountSummaryTextProps}
                        {...amountItemAmountBoxProps}
                      >
                        = {currency} {three_d_charge * item.quantity}
                      </Text>
                    </Box>
                  </Flex>
                ))}
              </VStack>
            </>
          )}

          {/* Ticket Subtotal */}
          {ticketSubtotal > 0 && (
            <>
              <Divider my="1rem" />
              <Flex w="full" justifyContent="space-between" mb="1rem">
                <Text
                  fontSize="md"
                  color="surface.400"
                  fontWeight={600}
                  {...amountSummaryTextProps}
                  {...subTotalTextProps}
                >
                  Ticket Subtotal
                </Text>
                <Text
                  fontSize="md"
                  color="surface.400"
                  fontWeight={600}
                  {...amountSummaryTextProps}
                  {...subTotalAmountTextProps}
                >
                  {currency} {ticketSubtotal}
                </Text>
              </Flex>
            </>
          )}

          {/* Food Section */}
          {foodBreakdown && foodBreakdown.length > 0 && (
            <>
              <Divider my="1rem" />
              <Text
                fontSize="md"
                fontWeight="600"
                color="surface.500"
                mb="0.5rem"
                {...amountSummaryTextProps}
              >
                Food & Beverages
              </Text>
              <VStack {...VStackProps}>
                {foodBreakdown.map((item, index) => (
                  <Flex
                    key={`food__${index}`}
                    w="full"
                    justifyContent="space-between"
                    {...amountItemFlexProps}
                  >
                    <Box>
                      <Text
                        fontSize={item.highlightItem ? "lg" : "md"}
                        color={
                          item.highlightItem ? "surface.500" : "surface.300"
                        }
                        {...amountItemTitleProps}
                      >
                        {item.title}
                        {item.showAmountUnitPrice && (
                          <Text
                            ml="0.5rem"
                            as="span"
                            {...amountItemUnitPriceProps}
                          >
                            ({item.quantity} X {item.amount})
                          </Text>
                        )}
                      </Text>
                    </Box>
                    <Box {...amountItemAmountBoxProps}>
                      <Text
                        fontWeight={500}
                        fontSize="md"
                        color="surface.300"
                        {...amountItemAmountBoxProps}
                      >
                        = {currency} {item.amount * item.quantity}
                      </Text>
                    </Box>
                  </Flex>
                ))}
              </VStack>

              {/* Food Subtotal */}
              {foodSubtotal > 0 && (
                <>
                  <Divider my="1rem" />
                  <Flex w="full" justifyContent="space-between" mb="1rem">
                    <Text
                      fontSize="md"
                      color="surface.400"
                      fontWeight={600}
                      {...subTotalTextProps}
                    >
                      Food Subtotal
                    </Text>
                    <Text
                      fontSize="md"
                      color="surface.400"
                      fontWeight={600}
                      {...subTotalAmountTextProps}
                    >
                      {currency} {foodSubtotal}
                    </Text>
                  </Flex>
                </>
              )}
            </>
          )}

          <Divider my="1rem" />

          {voucherDiscount > 0 && (
            <>
              <VStack {...VStackProps}>
                <Flex
                  w="full"
                  justifyContent="space-between"
                  {...amountItemFlexProps}
                >
                  <Box>
                    <Text
                      fontSize="md"
                      fontWeight={500}
                      color="surface.300"
                      {...amountItemTitleProps}
                    >
                      Voucher Discount{voucherLabel ? ` (${voucherLabel})` : ""}
                    </Text>
                  </Box>
                  <Box color="surface.300" {...amountItemAmountBoxProps}>
                    - {currency} {voucherDiscount}
                  </Box>
                </Flex>
              </VStack>
              <Divider my="1rem" />
            </>
          )}

          {payViaLoyalty && hasEnoughPoints && (
            <>
              <VStack {...VStackProps}>
                <Flex
                  w="full"
                  justifyContent="space-between"
                  {...amountItemFlexProps}
                >
                  <Box>
                    <Text
                      fontSize="md"
                      fontWeight={500}
                      color="surface.300"
                      {...amountItemTitleProps}
                    >
                      Loyalty Discount
                    </Text>
                  </Box>
                  <Box color="surface.300" {...amountItemAmountBoxProps}>
                    - {currency} {total}
                  </Box>
                </Flex>
              </VStack>
              <Divider my="1rem" />
            </>
          )}

          <Flex w="full" justifyContent="space-between" {...totalFlexProps}>
            <Text
              fontSize="lg"
              color="surface.500"
              fontWeight={500}
              {...totalTextProps}
            >
              Total Amount
            </Text>
            <Text
              fontSize="lg"
              color="surface.500"
              fontWeight={500}
              {...totalAmountTextProps}
            >
              {currency} {total - (payViaLoyalty ? total : 0)}
            </Text>
          </Flex>
        </Box>
      </Box>
    </>
  );
}
